import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import {
  RiCheckboxBlankCircleLine,
  RiCheckboxCircleLine,
  RiEyeLine,
} from 'react-icons/ri';
import { SelectOption } from '../../../../../../../components/Form/ReactSelect';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { IActivityListItem } from '../../../../../../../services/Activities/ListActivitiesService';

export interface IActivity extends IActivityListItem {
  formattedCreatedAt: string;
}

interface IActivityTableProps extends BoxProps {
  activities: IActivity[];
  currentPage?: number;
  filterBy?: string;
  filterByOptions?: SelectOption[];
  loadAsyncOptions: (inputValue: string) => void;
  onChangeFilterBy?: (value: string) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'name' | 'createdAt') => void;
  order?: 'ASC' | 'DESC';
  sortBy?: string;
  totalPages?: number;
}

export const ActivityTable = ({
  activities,
  currentPage,
  filterBy,
  filterByOptions,
  loadAsyncOptions,
  onChangeFilterBy,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  order,
  sortBy,
  totalPages,
  ...rest
}: IActivityTableProps): JSX.Element => {
  const filterOptions = useMemo(
    () =>
      filterByOptions ?? [
        { label: 'Nome', value: 'name' },
        { label: 'Local', value: 'spotId' },
      ],
    [filterByOptions],
  );

  const sortByOptions = useMemo(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'Data de cadastro',
        value: 'createdAt',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <TableFilters
        filterBy={filterBy}
        filterInputType={
          filterBy && ['ventureId', 'spotId'].includes(filterBy)
            ? 'asyncSelect'
            : 'input'
        }
        filterByOptions={filterOptions}
        loadAsyncOptions={loadAsyncOptions}
        onFilterAsync={onFilter}
        onChangeFilterBy={onChangeFilterBy}
        onFilter={onFilter}
        onOrder={onOrder}
        onSort={(sort) => !!onSort && onSort(sort as 'name' | 'createdAt')}
        order={order}
        sortBy={sortBy}
        sortByOptions={sortByOptions}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th textAlign="center">Ativo</Th>

            <Th>Atividade</Th>

            <Th>Local</Th>

            <Th textAlign="center">Área de convivência</Th>

            <Th textAlign="center">Data de cadastro</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {activities.map((activity) => (
            <Tr key={activity.id}>
              <Td textAlign="center">
                {activity.isActive ? (
                  <Icon as={RiCheckboxCircleLine} color="green" />
                ) : (
                  <Icon as={RiCheckboxBlankCircleLine} color="gray.400" />
                )}
              </Td>

              <Td>
                <Box>
                  <Text fontWeight="bold">{activity.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {activity.spot.name}
                  </Text>
                </Box>
              </Td>

              <Td>{activity.spot.name}</Td>

              <Td textAlign="center">
                {activity.spot.isLivingArea ? (
                  <Icon as={RiCheckboxCircleLine} color="green" />
                ) : (
                  <Icon as={RiCheckboxBlankCircleLine} color="gray.400" />
                )}
              </Td>

              <Td textAlign="center">{activity.formattedCreatedAt}</Td>

              <Td textAlign="right">
                <LinkButton
                  px={0}
                  to={{
                    pathname: '/activities/details',
                    state: {
                      activityId: activity.id,
                    },
                  }}
                >
                  <Icon as={RiEyeLine} fontSize="16" />
                </LinkButton>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
